import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { Table, Image, Popup } from "semantic-ui-react";
import styles from "./ItemsList.module.scss";
import {
  Body3,
  Heading5,
} from "../../../components/value-cssmodules/TextComponent";
import { CartData } from "../../../types/siteTypes";
import { useDispatch, useSelector } from "react-redux";
import { addToList, removeFromList } from "../../../actions/cartActions";
import { setAlert } from "../../../actions/alertActions";
import { MdAddShoppingCart } from "react-icons/md";
import { BsTrash } from "react-icons/bs";
import { FiEdit2 } from "react-icons/fi";
import { cartSelector } from "../../../utils/selectors";
import ListHeader from "./ListHeader";
import { formatEur } from "../../../utils/patternPricing";
import SizeSelectionModal from "../../../components/SizeSelectionModal";
import {
  formatPatternSizeLabel,
  getOrderedPatternSizes,
  resolveCartSelectedSize,
} from "../../../utils/patternSize";

type Props = {
  items: CartData[];
  type: string;
  addToCartAction?: boolean;
};

const ItemsList: React.FC<Props> = ({
  items,
  type = "cart",
  addToCartAction = false,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const cart = useSelector((state: any) => cartSelector(state));
  const [sizeSelectionState, setSizeSelectionState] = useState<{
    item: CartData;
    selectableSizes: string[];
  } | null>(null);
  const [sizeEditState, setSizeEditState] = useState<{
    item: CartData;
    availableSizes: string[];
    unavailableSizes: string[];
  } | null>(null);
  const modalSizes = useMemo(() => sizeSelectionState?.selectableSizes || [], [
    sizeSelectionState,
  ]);
  const editModalSizes = useMemo(() => sizeEditState?.availableSizes || [], [
    sizeEditState,
  ]);

  const getDisplaySize = (item: CartData) => {
    const selectedSize =
      item.selectedSize || (item.sizes?.length === 1 ? item.sizes[0] : "");
    return selectedSize ? formatPatternSizeLabel(selectedSize) : "-";
  };

  const moveItemToCart = (item: CartData, selectedSizes: string[]) => {
    dispatch(removeFromList(item.id, type));
    dispatch(
      addToList(
        item.id,
        "cart",
        selectedSizes.length === 1 ? selectedSizes[0] : selectedSizes
      )
    );
  };

  const getSelectableSizesForPattern = (item: CartData) => {
    const availableSizes = getOrderedPatternSizes(item.sizes || []);
    const sizesAlreadyInCart = new Set(
      cart
        .filter((cartItem: CartData) => cartItem.id === item.id)
        .map((cartItem: CartData) => resolveCartSelectedSize(cartItem))
        .filter(Boolean)
    );

    return availableSizes.filter((size) => !sizesAlreadyInCart.has(size));
  };

  const removeItem = (item: CartData) => {
    if (type === "cart") {
      dispatch(removeFromList(item.id, type, resolveCartSelectedSize(item) || null));
      return;
    }

    dispatch(removeFromList(item.id, type));
  };

  const openSizeEditor = (item: CartData) => {
    if (type !== "cart") {
      return;
    }

    const availableSizes = getOrderedPatternSizes(item.sizes || []);
    if (availableSizes.length <= 1) {
      return;
    }

    const currentSize = resolveCartSelectedSize(item);
    const unavailableSizes = Array.from(
      new Set<string>(
        cart
          .filter((cartItem: CartData) => cartItem.id === item.id)
          .map((cartItem: CartData) => resolveCartSelectedSize(cartItem))
          .filter(
            (size: string): size is string => !!size && size !== currentSize
          )
      )
    );

    setSizeEditState({
      item,
      availableSizes,
      unavailableSizes,
    });
  };

  const updateCartItemSize = (selectedSizes: string[]) => {
    if (!sizeEditState) {
      return;
    }

    const nextSize = selectedSizes[0];
    const currentSize = resolveCartSelectedSize(sizeEditState.item);

    if (!nextSize || nextSize === currentSize) {
      setSizeEditState(null);
      return;
    }

    dispatch(removeFromList(sizeEditState.item.id, "cart", currentSize || null));
    dispatch(addToList(sizeEditState.item.id, "cart", nextSize));
    dispatch(setAlert(`Zmieniono rozmiar na ${formatPatternSizeLabel(nextSize)}`, "success"));
    setSizeEditState(null);
  };
console.log("Rendering ItemsList with items:", items);

  return (
    <>
      <Table singleLine className={styles.self}>
        <ListHeader />

        <Table.Body className={styles.body}>
          {items.map((item: CartData) => {
            const selectableSizes = getSelectableSizesForPattern(item);
            const canAddToCart = selectableSizes.length > 0;
            const rowKey =
              type === "cart"
                ? `${type}-${item.id}-${resolveCartSelectedSize(item)}`
                : `${type}-${item.id}`;

            return (
              <Table.Row key={rowKey}>
                <Table.Cell>
                  <Link
                    to={`/pattern/${item.id}`}
                    className={styles.patternImageLink}
                    aria-label={item.name}
                  >
                    <Image
                      src={item.photo ? item.photo.url : "/photos/no-photo.jpg"}
                      size={"small"}
                      className={styles.photo}
                    />
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <Heading5>
                    <Link to={`/pattern/${item.id}`} className={styles.patternNameLink}>
                      {item.name}
                    </Link>
                  </Heading5>
                  <Body3>
                    <Link to={`/seller/${item.user}`}>
                      {item.profileHandle ? item.profileHandle.substring(0, 44) : intl.formatMessage({ id: "cart.unknownSeller", defaultMessage: "Unknown" })}
                    </Link>
                  </Body3>
                </Table.Cell>
                <Table.Cell className={styles.sizeCell}>
                  <div className={styles.sizeCellContent}>
                    <span>{getDisplaySize(item)}</span>
                    {type === "cart" && getOrderedPatternSizes(item.sizes || []).length > 1 && (
                      <Popup
                        content={intl.formatMessage({
                          id: "cart.changeSize",
                        })}
                        position="top center"
                        trigger={
                          <button
                            type="button"
                            className={styles.sizeEditButton}
                            aria-label={intl.formatMessage({
                              id: "cart.changeSize",
                            })}
                            onClick={() => openSizeEditor(item)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                        }
                      />
                    )}
                  </div>
                </Table.Cell>

                <Table.Cell>
                  <div className={styles.priceCell}>
                    {item.showPromotionInfo &&
                    item.originalPrice !== null &&
                    item.originalPrice !== undefined ? (
                      <>
                        <div className={styles.priceRow}>
                          <span className={styles.oldPrice}>{formatEur(item.originalPrice)}</span>
                          <span className={styles.newPrice}>{formatEur(item.price)}</span>
                        </div>
                        {item.showOmnibusInfo &&
                          item.omnibusLowestPrice !== null &&
                          item.omnibusLowestPrice !== undefined && (
                            <span className={styles.omnibusLine}>
                              {intl.formatMessage(
                                { id: "cart.omnibusInfo", defaultMessage: "Lowest price in 30 days before discount: {price}" },
                                { price: formatEur(item.omnibusLowestPrice) }
                              )}
                            </span>
                          )}
                      </>
                    ) : (
                      <>
                        <span>{formatEur(item.price)}</span>
                        {item.showOmnibusInfo &&
                          item.omnibusLowestPrice !== null &&
                          item.omnibusLowestPrice !== undefined && (
                            <span className={styles.omnibusLine}>
                              {intl.formatMessage(
                                { id: "cart.omnibusInfo", defaultMessage: "Lowest price in 30 days before discount: {price}" },
                                { price: formatEur(item.omnibusLowestPrice) }
                              )}
                            </span>
                          )}
                      </>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell className={styles.icons}>
                  {addToCartAction && canAddToCart && (
                    <Popup
                      content={intl.formatMessage({
                        id: "cart.addToCart",
                      })}
                      position="top center"
                      trigger={
                        <span>
                          <MdAddShoppingCart
                            aria-label={intl.formatMessage({
                              id: "cart.addToCart",
                            })}
                            size={24}
                            onClick={() => {
                              if (selectableSizes.length > 1) {
                                setSizeSelectionState({
                                  item,
                                  selectableSizes,
                                });
                                return;
                              }
                              moveItemToCart(item, [selectableSizes[0]]);
                            }}
                          />
                        </span>
                      }
                    />
                  )}
                  <Popup
                    content={intl.formatMessage({
                      id: `cart.tableTitle${type}.removeFromList`,
                    })}
                    position="top center"
                    trigger={
                      <span>
                        <BsTrash
                          aria-label={intl.formatMessage({
                            id: `cart.tableTitle${type}.removeFromList`,
                          })}
                          size={24}
                          onClick={() => removeItem(item)}
                        />
                      </span>
                    }
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      <SizeSelectionModal
        open={!!sizeSelectionState}
        patternName={sizeSelectionState?.item.name}
        patternImageUrl={
          sizeSelectionState?.item.photo
            ? sizeSelectionState.item.photo.thumbnailUrl ||
            sizeSelectionState.item.photo.url
            : "/photos/no-photo.jpg"
        }
        sizes={modalSizes}
        multiple={true}
        onClose={() => setSizeSelectionState(null)}
        onConfirm={(selectedSizes) => {
          if (sizeSelectionState) {
            moveItemToCart(sizeSelectionState.item, selectedSizes);
          }
          setSizeSelectionState(null);
        }}
      />
      <SizeSelectionModal
        open={!!sizeEditState}
        patternName={sizeEditState?.item.name}
        patternImageUrl={
          sizeEditState?.item.photo
            ? sizeEditState.item.photo.thumbnailUrl ||
            sizeEditState.item.photo.url
            : "/photos/no-photo.jpg"
        }
        sizes={editModalSizes}
        unavailableSizes={sizeEditState?.unavailableSizes || []}
        multiple={false}
        onClose={() => setSizeEditState(null)}
        onConfirm={(selectedSizes) => updateCartItemSize(selectedSizes)}
      />
    </>
  );
};

export default ItemsList;
